import React from "react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "../mock";
import { ShieldCheck, Github, Twitter, Instagram, MessageCircle, EyeOff, KeyRound } from "lucide-react";

const Footer = () => {
  return (
    <footer className="relative border-t border-white/5 bg-black text-white/60">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-3">
              <div
                className="h-14 w-14 rounded-xl overflow-hidden flex-shrink-0"
                style={{
                  backgroundImage: `url(${LOGO_URL}?v=2)`,
                  backgroundSize: "183% 159%",
                  backgroundPosition: "center top",
                  backgroundRepeat: "no-repeat",
                }}
                aria-label="Phantom Industries"
                role="img"
              />
              <div>
                <div className="font-display text-white font-semibold">Phantom Industries</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-purple-300/80">
                  Privacy Perfected. Phantom Protected.
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed max-w-sm">
              Purpose-built Google Pixel devices running hardened GrapheneOS builds.
              Designed and configured in Australia for people who value privacy above all.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href="https://t.me/Phantom0091" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center hover:border-purple-500/50 hover:text-white transition-colors">
                <MessageCircle className="h-4 w-4" />
              </a>
              <a href="#" aria-label="Twitter" className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center hover:border-purple-500/50 hover:text-white transition-colors">
                <Twitter className="h-4 w-4" />
              </a>
              <a href="#" aria-label="Instagram" className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center hover:border-purple-500/50 hover:text-white transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="#" aria-label="GitHub" className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center hover:border-purple-500/50 hover:text-white transition-colors">
                <Github className="h-4 w-4" />
              </a>
            </div>
          </div>

          <FooterCol title="Shop">
            <FooterLink to="/shop">All Devices</FooterLink>
            <FooterLink to="/product/pixel-9-pro-xl">Pixel 9 Pro XL</FooterLink>
            <FooterLink to="/product/pixel-9-pro">Pixel 9 Pro</FooterLink>
            <FooterLink to="/product/pixel-8a">Pixel 8a</FooterLink>
          </FooterCol>

          <FooterCol title="Security">
            <FooterLink to="/tiers">Compare Tiers</FooterLink>
            <FooterLink to="/tiers#degoogled">Degoogled</FooterLink>
            <FooterLink to="/tiers#stealth">Stealth</FooterLink>
            <FooterLink to="/tiers#phantom">Phantom</FooterLink>
          </FooterCol>

          <FooterCol title="Company">
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
            <FooterLink to="/lookup">Order lookup</FooterLink>
            <a href="#" className="block py-1.5 text-sm hover:text-white transition-colors">Warranty</a>
            <a href="#" className="block py-1.5 text-sm hover:text-white transition-colors">Shipping</a>
          </FooterCol>
        </div>

        {/* Trust row: no-track badge + PGP */}
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <div
            className="flex items-center gap-3 rounded-2xl border border-purple-500/20 bg-purple-500/5 px-5 py-4"
            data-testid="footer-notrack-badge"
          >
            <div className="h-9 w-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
              <EyeOff className="h-4 w-4 text-purple-300" />
            </div>
            <div>
              <div className="font-display text-sm font-semibold text-white">
                We don't track you.
              </div>
              <div className="text-xs text-white/50 leading-relaxed mt-0.5">
                No analytics, no advertising cookies, no third-party pixels on this site.
              </div>
            </div>
          </div>

          <a
            href="/phantom-industries-pgp.asc"
            download="phantom-industries-pgp.asc"
            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 hover:border-purple-500/40 hover:bg-purple-500/5 transition-colors group"
            data-testid="footer-pgp-download"
          >
            <div className="h-9 w-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
              <KeyRound className="h-4 w-4 text-purple-300" />
            </div>
            <div className="flex-1">
              <div className="font-display text-sm font-semibold text-white group-hover:text-white">
                PGP public key
              </div>
              <div className="text-xs text-white/50 leading-relaxed mt-0.5">
                Download <span className="font-mono text-purple-300">phantom-industries-pgp.asc</span> to send us encrypted mail.
              </div>
            </div>
          </a>
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-white/40">
            <ShieldCheck className="h-3.5 w-3.5 text-purple-400" />
            <span>Designed and configured in Australia. Ships worldwide.</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/40">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <span className="text-white/20">·</span>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <span className="text-white/20">·</span>
            <Link to="/lookup" className="hover:text-white transition-colors" data-testid="footer-lookup-link">Order lookup</Link>
          </div>
          <div className="text-xs text-white/40">
            © {new Date().getFullYear()} Phantom Industries. All rights reserved. GrapheneOS ™ is a trademark of its owners.
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, children }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);

const FooterLink = ({ to, children }) => (
  <Link to={to} className="block py-1.5 text-sm hover:text-white transition-colors">
    {children}
  </Link>
);

export default Footer;
